import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import WorkBagHeader from "../../components/WorkBag/WorkBagHeader.tsx";
import StickyNavbar from "../../components/WorkBag/StickyNavbar.tsx";
import WorkOfferGrid from "../../components/WorkBag/WorkOfferGrid.tsx";
import JobboardRegistrationFlow from "../../components/WorkBag/JobboardRegistrationFlow.tsx";
import { getSkills, getAllWorkOffers, loginWithGoogleJobBoard } from "../../store/actions";
import { fireAnalytics } from "../../utils/firebase";
import Logo from "../../assets/images/logo-navbar.png";
import { getSessionId, getDeviceCategory, getPreviousPage } from "../../utils/analyticsHelpers.ts";
import { useScrollDetection } from "../../hooks/useScrollDetection.ts";
import styles from "./WorkBagNew.module.scss";
import { WorkBagProps, RootState } from "../../types/workBag";

const WorkBag: React.FC<WorkBagProps> = React.memo(({
  getSkills,
  getAllWorkOffers,
  allJobRequests,
  loginWithGoogleJobBoard: loginWithGoogleJobBoardAction,
}) => {
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationInitialEmail, setRegistrationInitialEmail] = useState<string | undefined>();
  const { isScrolled } = useScrollDetection(100);
  const navigate = useNavigate();

  useEffect(() => {
    getSkills();
    getAllWorkOffers();
    fireAnalytics("page_work_bag_view", {
      session_id: getSessionId(),
      source: getPreviousPage() ? "navigation" : "direct",
      previous_page: getPreviousPage(),
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "work_bag",
    });
  }, [getSkills, getAllWorkOffers]);

  const handleInscribirme = useCallback((email?: string) => {
    setRegistrationInitialEmail(email);
    setShowRegistrationModal(true);
  }, []);

  const handleRegistrationClose = useCallback(() => {
    setShowRegistrationModal(false);
    setRegistrationInitialEmail(undefined);
  }, []);

  const scrollToOffers = useCallback(() => {
    const offersEl = document.querySelector(`.${styles.offersSection}`);
    if (offersEl) {
      offersEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    fireAnalytics("page_work_offers_view", {
      session_id: getSessionId(),
      source: "navigation",
      previous_page: "/bolsadetrabajo",
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "work_offers",
    });
  }, []);

  return (
    <div className={styles.mainContainer}>
      {isScrolled && (
        <StickyNavbar
          activeSection="offers"
          onScrollToSection={(section) => section === "offers" && scrollToOffers()}
          onInscribirmeClick={handleInscribirme}
          logo={Logo}
          totalOffers={allJobRequests?.total ?? 0}
        />
      )}

      <WorkBagHeader
        onInscribirme={handleInscribirme}
        loginWithGoogleJobBoard={loginWithGoogleJobBoardAction}
      />

      <main className={styles.mainContent}>
        <div className={styles.contentContainer}>
          <section className={styles.offersSection}>
            <WorkOfferGrid scrollToForm={handleInscribirme} />
          </section>
        </div>
      </main>

      {showRegistrationModal && (
        <JobboardRegistrationFlow
          isOpen={showRegistrationModal}
          onClose={handleRegistrationClose}
          initialEmail={registrationInitialEmail}
          registrationEntry="header"
          onSuccess={(meta) => {
            handleRegistrationClose();
            if (meta?.flow === "returning-login") return;
            if (meta?.flow === "needs-jobboard-onboarding") {
              navigate("/bolsadetrabajo/completar-perfil");
              return;
            }
            navigate("/bolsadetrabajo/registro-exitoso");
          }}
        />
      )}
    </div>
  );
});

const mapStateToProps = (state: RootState) => ({
  allJobRequests: state.register.allJobRequests,
});

export default connect(mapStateToProps, {
  getSkills,
  getAllWorkOffers,
  loginWithGoogleJobBoard,
})(WorkBag);
